import React, { useMemo } from 'react';
import { styled } from '@compiled/react';
import { TitleBlock, CardAction } from '@atlaskit/smart-card';
import { CardSSR as Card } from '@atlaskit/smart-card/ssr';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { CompassComponentLink } from '../../../../common/services/search-compass-components/types.tsx';
import { SubtleText } from '../styled.tsx';
import messages from './messages.tsx';
import RepositoryLinksPopup from './repository-links-popup/index.tsx';

const RE_BITBUCKET_REPOSITORY = /^https?:\/\/bitbucket\.org\//;
export const LINK_DISPLAY_LIMIT = 1;

export function RepositoryCell({ repositoryLinks }: { repositoryLinks: CompassComponentLink[] }) {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const bubbledRepositoryLinks = useMemo(() => bubbleBitbucket(repositoryLinks), [repositoryLinks]);

	if (bubbledRepositoryLinks.length === 0) {
		return <SubtleText>{formatMessage(messages.noRepository)}</SubtleText>;
	}

	const displayedRepositoryLinks = bubbledRepositoryLinks.slice(0, LINK_DISPLAY_LIMIT);
	const remainingRepositoryLinks = bubbledRepositoryLinks.slice(LINK_DISPLAY_LIMIT);

	return (
		<>
			{displayedRepositoryLinks.map((link, index) => (
				<LinkContainer key={index}>
					<Card
						testId="project-directories.components.compass-table.cells.repository.smart-link"
						url={link.url}
						appearance="inline"
						placeholder={link.name || link.url}
						ui={{
							hideBackground: true,
							hideElevation: true,
							hidePadding: true,
							clickableContainer: true,
						}}
						onClick={() => {
							const analyticsEvent = createAnalyticsEvent({
								action: 'clicked',
								actionSubject: 'link',
							});

							fireUIAnalytics(analyticsEvent, 'repositoryLink');
						}}
						actionOptions={{
							hide: false,
							exclude: [CardAction.ChangeStatusAction],
						}}
					>
						<TitleBlock text={link.name} maxLines={1} hideIcon />
					</Card>

					{/* display remaining links popup only the last entry */}
					{index === displayedRepositoryLinks.length - 1 && remainingRepositoryLinks.length > 0 && (
						<RepositoryLinksPopup repositories={remainingRepositoryLinks} />
					)}
				</LinkContainer>
			))}
		</>
	);
}

export function filterRepositoryLinks(links: CompassComponentLink[]) {
	return links.filter((link) => link.type === 'REPOSITORY');
}

export function bubbleBitbucket(repositoryLinks: CompassComponentLink[], sort = true) {
	const bitbucketLinks = repositoryLinks.filter((link) => RE_BITBUCKET_REPOSITORY.test(link.url));
	const remainingLinks = repositoryLinks.filter((link) => !RE_BITBUCKET_REPOSITORY.test(link.url));

	if (sort) {
		const comparator = (a: CompassComponentLink, b: CompassComponentLink) => {
			if (!!a.name && !b.name) {
				return -1;
			}

			if (!a.name && !!b.name) {
				return 1;
			}

			return (a.name || a.url).localeCompare(b.name || b.url);
		};

		bitbucketLinks.sort(comparator);
		remainingLinks.sort(comparator);
	}

	remainingLinks.unshift(...bitbucketLinks);

	return remainingLinks;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LinkContainer = styled.div({
	display: 'inline-flex',
	alignItems: 'center',
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[data-smart-link-container="true"]': {
		display: 'inline-flex',
	},
});
